import { useState, useEffect } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import UserInfo from './userInfo.js'
import UserSettings from './userSettings.js'
import UserSubscription from './userSubscription.js'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../../api/axiosInstance.js'
import Swal from 'sweetalert2'

const UserDetail = () => {
  const theme = useTheme()
  const dc = theme.palette.designTokens
  const [params] = useState(useParams())
  const navigate = useNavigate()
  const [userInfo, setUserInfo] = useState({})
  const [activeTab, setActiveTab] = useState('info')

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await api.get(`users/${params.userId}`)
        setUserInfo(response.data)
      } catch (error) {
        console.error('Kullanıcı bilgileri alınırken hata oluştu:', error)
      }
    }
    fetchUserInfo()
  }, [])

  const deleteUser = () => {
    Swal.fire({
      title: 'Silmek istediğinizden emin misiniz',
      icon: 'warning',
      text: 'Kullanıcı sistemden tamamen silinecektir',
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonText: 'Evet silmek istiyorum',
      cancelButtonText: 'Hayır',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          await api.delete(`users/${userInfo.id}`)
          Swal.fire({
            icon: 'success',
            title: 'Kullanıcı Silindi',
            text: 'Başarılı şekilde silindi',
            confirmButtonText: 'Tamam',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          })
          navigate('/admin/users')
        }
      })
      .catch((error) => {
        console.log(error)
        Swal.fire({
          icon: 'error',
          title: 'Hatalı İşlem',
          text: error?.response?.data?.message || 'Bir hata oluştu',
          confirmButtonText: 'Tamam',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        })
      })
  }

  const sendVerificationEmail = async () => {
    try {
      await api.post(`users/${userInfo.id}/send-verification-email`)
      Swal.fire({
        icon: 'success',
        title: 'Doğrulama maili gönderildi',
        text: `${userInfo.email} adresine e-posta doğrulama bağlantısı gönderildi.`,
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gönderilemedi',
        text: error?.response?.data?.message || 'Doğrulama maili gönderilirken bir hata oluştu',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      })
    }
  }

  const cardSx = {
    background: dc.card,
    border: `1px solid ${dc.cardBorder}`,
    borderRadius: '20px',
    backdropFilter: 'none',
  }

  // Navy pill tag used in the header
  const Tag = ({ children, color, onClick, title }) => (
    <Box
      component="span"
      onClick={onClick}
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: '12px',
        py: '7px',
        borderRadius: '8px',
        fontSize: 13,
        fontWeight: 600,
        background: dc.chipBg,
        border: `1px solid ${dc.chipBorder}`,
        color: color || dc.chipText,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        ...(onClick && {
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 14px rgba(16,185,129,0.25)',
          },
        }),
      }}
    >
      {children}
    </Box>
  )

  const tabs = [
    { id: 'info', label: 'Bilgiler', emoji: '🪪' },
    { id: 'settings', label: 'Ayarlar', emoji: '⚙️' },
    { id: 'package', label: 'Paket', emoji: '👑' },
  ]

  const createdAt = userInfo.created_at
    ? String(userInfo.created_at).split('T')[0]
    : '—'

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ width: '100%' }}
    >
      {/* Header Card with Avatar */}
      <Box sx={{ ...cardSx, overflow: 'hidden', mb: 3 }}>
        {/* Banner */}
        <Box sx={{ height: 148, background: dc.gradient, position: 'relative' }}>
          {/* Avatar */}
          <Box
            sx={{
              position: 'absolute',
              left: 40,
              bottom: -56,
              width: 112,
              height: 112,
              borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #34d399, #059669)',
              border: `4px solid ${dc.card}`,
              boxShadow: '0 0 24px rgba(52,211,153,0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 44,
              fontWeight: 800,
              color: '#ffffff',
            }}
          >
            {userInfo.name?.charAt(0).toUpperCase() || '?'}
          </Box>
          {/* Name */}
          <Typography
            sx={{
              position: 'absolute',
              left: 172,
              top: 44,
              fontSize: 30,
              fontWeight: 800,
              color: '#ffffff',
              letterSpacing: '-0.02em',
            }}
          >
            {userInfo.name}
          </Typography>
        </Box>

        {/* Tags row */}
        <Box
          sx={{
            pt: '34px',
            pr: '40px',
            pb: '24px',
            pl: '172px',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {userInfo.role && (
            <Tag>💼 {userInfo.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</Tag>
          )}
          <Tag>📅 {createdAt}</Tag>
          <Tag color={userInfo.status ? '#7dd3b8' : '#f87171'}>
            {userInfo.status ? '✅ Aktif' : '⛔ Pasif'}
          </Tag>
          <Tag
            color={userInfo.isEmailVerified ? '#7dd3b8' : '#fbbf24'}
            onClick={userInfo.isEmailVerified ? undefined : sendVerificationEmail}
            title={
              userInfo.isEmailVerified
                ? undefined
                : 'Doğrulama maili göndermek için tıklayın'
            }
          >
            {userInfo.isEmailVerified ? '✉️ E-posta onaylı' : '✉️ E-posta onaysız'}
          </Tag>
        </Box>
      </Box>

      {/* Tabs + actions */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {/* Segmented pill tabs */}
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            p: '6px',
            gap: '4px',
            borderRadius: '16px',
            background: dc.card,
            border: `1px solid ${dc.cardBorder}`,
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id
            return (
              <Box
                key={tab.id}
                component="button"
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.75,
                  py: 1.5,
                  border: 'none',
                  background: 'transparent',
                  borderRadius: '11px',
                  fontFamily: 'inherit',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  color: isActive ? dc.onGradient : '#94a3b8',
                  transition: 'color 0.25s ease',
                  '&:hover': { color: isActive ? dc.onGradient : dc.accent },
                }}
              >
                {isActive && (
                  <Box
                    component={motion.div}
                    layoutId="userDetailTabIndicator"
                    transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: -1,
                      borderRadius: '11px',
                      background: dc.gradientBtn,
                      boxShadow: '0 4px 16px rgba(16,185,129,0.35)',
                    }}
                  />
                )}
                <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                  {tab.emoji}
                </Box>
                <span>{tab.label}</span>
              </Box>
            )
          })}
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Box
            component="button"
            onClick={() => navigate(`/admin/userMonitors/${params.userId}`)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              px: '18px',
              py: '11px',
              border: 'none',
              borderRadius: '12px',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              color: dc.onGradient,
              background: dc.gradientBtn,
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 14px rgba(16,185,129,0.35)',
              },
            }}
          >
            🖥️ Monitörler
          </Box>
          <Box
            component="button"
            onClick={() => deleteUser()}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              px: '18px',
              py: '11px',
              borderRadius: '12px',
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              color: '#f87171',
              background: 'rgba(239,68,68,0.08)',
              border: '1px solid rgba(239,68,68,0.35)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 14px rgba(239,68,68,0.25)',
                background: 'rgba(239,68,68,0.14)',
              },
            }}
          >
            🗑️ Sil
          </Box>
        </Box>
      </Box>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'info' && (
          <motion.div
            key="info"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <UserInfo userInfo={userInfo} />
          </motion.div>
        )}
        {activeTab === 'settings' && (
          <motion.div
            key="settings"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <UserSettings userInfo={userInfo} />
          </motion.div>
        )}
        {activeTab === 'package' && (
          <motion.div
            key="package"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <UserSubscription userInfo={userInfo} />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default UserDetail
